import React, { useMemo, useCallback } from "react";
import { tv } from "tailwind-variants";
import { motion } from "framer-motion";
import type { MenuItem } from "../types/popular";
import clsx from "clsx";

// Animation variants for better reusability
const fadeInUp = {
  hidden: { opacity: 0, y: 20 },
  show: { opacity: 1, y: 0, transition: { duration: 0.3 } },
};

const staggerContainer = {
  hidden: { opacity: 0 },
  show: {
    opacity: 1,
    transition: {
      staggerChildren: 0.1,
    },
  },
};

/* ------------------ Constants ------------------ */

const DEFAULT_CURRENCY = "USD";
const DEFAULT_LOCALE = "en-US";
const FALLBACK_IMAGE_SIZE = 300;

/* ------------------ Utilities ------------------ */

// Safe parsing of price from string or number with null/undefined handling
const parsePrice = (price?: string | number | null): number => {
  if (price == null) return 0;
  if (typeof price === "number") return price;
  const parsed = parseFloat(String(price).replace(/[^0-9.-]/g, ""));
  return isNaN(parsed) ? 0 : parsed;
};

// i18n-friendly price formatting with error handling
const formatPrice = (value: number, currency = DEFAULT_CURRENCY, locale = DEFAULT_LOCALE) => {
  try {
    return new Intl.NumberFormat(locale, {
      style: "currency",
      currency,
    }).format(value);
  } catch (error) {
    return `$${value.toFixed(2)}`;
  }
};

// Placeholder fallback image
const fallbackImage = (size: number = FALLBACK_IMAGE_SIZE) =>
  `https://via.placeholder.com/${size}x${size}/f3f4f6/9ca3af?text=No+Image`;

/* ------------------ Icon Components ------------------ */

const CloseIcon: React.FC = () => (
  <svg fill="none" stroke="currentColor" viewBox="0 0 24 24" className="w-4 h-4">
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
  </svg>
);

/* ------------------ SafeImage Component ------------------ */

interface SafeImageProps {
  src?: string;
  alt: string;
  itemName?: string;
  fallbackSize?: number;
  className?: string;
  loading?: "lazy" | "eager";
}

const SafeImage: React.FC<SafeImageProps> = React.memo(
  ({ src, alt, itemName, fallbackSize = FALLBACK_IMAGE_SIZE, className, loading = "lazy" }) => {
    const [loaded, setLoaded] = React.useState(false);
    const [hasError, setHasError] = React.useState(false);
    const fallbackImg = fallbackImage(fallbackSize);

    // Get first letter of item name for fallback
    const firstLetter = itemName?.charAt(0)?.toUpperCase() || alt?.charAt(0)?.toUpperCase() || "?";

    return (
      <div className="relative w-full h-full">
        {!loaded && !hasError && (
          <div className="absolute inset-0 bg-gray-200 animate-pulse rounded-t-lg" />
        )}
        
        {hasError || !src ? (
          // Show first letter fallback - perfectly centered in image container
          <div className="absolute inset-0 w-full h-full bg-gradient-to-br from-orange-400 to-orange-600 flex items-center justify-center rounded-t-lg">
            <span className="text-white font-bold text-4xl sm:text-5xl lg:text-6xl select-none">
              {firstLetter}
            </span>
          </div>
        ) : (
          <img
            src={src}
            alt={alt}
            loading={loading}
            decoding="async"
            className={clsx(
              "object-cover transition-opacity duration-300 w-full h-full",
              className,
              { "opacity-0": !loaded, "opacity-100": loaded }
            )}
            onLoad={() => setLoaded(true)}
            onError={() => {
              setHasError(true);
              setLoaded(true);
            }}
          />
        )}
      </div>
    );
  }
);
SafeImage.displayName = "SafeImage";

/* ------------------ Tailwind Variants ------------------ */

const card = tv({
  base: "rounded-2xl shadow-sm transition cursor-pointer overflow-hidden bg-white flex flex-col w-full h-full group focus:outline-none focus:ring-2 focus:ring-orange-400",
  variants: {
    variant: {
      basic: "border border-gray-200",
      classic: "shadow-md",
      prime: "shadow-xl",
    },
  },
});

const imageContainer = tv({
  base: "relative aspect-[4/3] w-full flex items-center justify-center overflow-hidden",
});

const contentContainer = tv({
  base: "flex-1 flex flex-col justify-between",
  variants: {
    variant: {
      basic: "p-3 sm:p-4",
      classic: "p-4 sm:p-5",
      prime: "p-5 sm:p-6",
    },
  },
});

/* ------------------ Types ------------------ */

type ItemCallback = (item: MenuItem) => void;

export type PopularItemCardVariant = "basic" | "classic" | "prime";

export interface PopularItemCardProps {
  item: MenuItem;
  variant?: PopularItemCardVariant;
  onItemClick?: ItemCallback;
  onAddToCart?: ItemCallback;
  onRemove?: ItemCallback;
  showPrice?: boolean;
  showDescription?: boolean;
  showRating?: boolean;
  showBadges?: boolean;
  showAddToCart?: boolean;
  showRemove?: boolean;
  className?: string;
}

/* ------------------ Main Component ------------------ */

const PopularItemCard: React.FC<PopularItemCardProps> = React.memo(
  ({
    item,
    variant = "basic",
    onItemClick,
    onAddToCart,
    onRemove,
    showPrice = true,
    showDescription = true,
    showRating = false,
    showBadges = false,
    showAddToCart = true,
    showRemove = false,
    className,
  }) => {
    // Safe price parsing with null/undefined handling
    const price = useMemo(() => parsePrice(item?.price), [item?.price]);
    const formattedPrice = useMemo(() => formatPrice(price), [price]);

    // Safe event handlers with null checks
    const handleCardClick = useCallback(() => {
      if (item && onItemClick) {
        onItemClick(item);
      }
    }, [onItemClick, item]);

    const handleAddToCart = useCallback(
      (e: React.MouseEvent) => {
        e.stopPropagation();
        if (item && onAddToCart) {
          onAddToCart(item);
        }
      },
      [onAddToCart, item]
    );

    const handleRemove = useCallback(
      (e: React.MouseEvent) => {
        e.stopPropagation();
        if (item && onRemove) {
          onRemove(item);
        }
      },
      [onRemove, item]
    );

    const handleKeyPress = useCallback(
      (e: React.KeyboardEvent) => {
        if ((e.key === "Enter" || e.key === " ") && item && onItemClick) {
          e.preventDefault();
          onItemClick(item);
        }
      },
      [onItemClick, item]
    );

    // Early return if item is null/undefined
    if (!item) {
      return null;
    }

    /* ---------- Render ---------- */
    return (
      <motion.div
        className={clsx(card({ variant }), className)}
        onClick={handleCardClick}
        onKeyDown={handleKeyPress}
        role="button"
        tabIndex={0}
        aria-label={`View details for ${item.name}`}
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.3 }}
        whileHover={{
          y: -6,
          boxShadow:
            "0 12px 20px rgba(0,0,0,0.08), 0 6px 10px rgba(0,0,0,0.05)",
        }}
        whileTap={{ scale: 0.98 }}
      >
        <div className={imageContainer()}>
          <SafeImage 
            src={item.image} 
            alt={item.name || "Popular Item"} 
            itemName={item.name}
            className="transition-transform duration-300 group-hover:scale-105"
          />

          {showRemove && (
            <motion.button
              onClick={handleRemove}
              aria-pressed="false"
              aria-label={`Remove ${item.name} from popular items`}
              className="absolute top-2 right-2 sm:top-3 sm:right-3 w-8 h-8 bg-red-500 hover:bg-red-600 text-white rounded-full shadow-lg flex items-center justify-center transition-colors z-10"
              initial={{ scale: 0, rotate: -180 }}
              animate={{ scale: 1, rotate: 0 }}
              transition={{ delay: 0.1, duration: 0.4, type: "spring" }}
              whileHover={{ scale: 1.1 }}
              whileTap={{ scale: 0.9 }}
            >
              <CloseIcon />
            </motion.button>
          )}

          {/* Badges */}
          {showBadges && (
            <div className="absolute top-2 left-2 sm:top-3 sm:left-3 flex flex-col gap-2">
              {item.food_type === "veg" && (
                <motion.span
                  className="px-2 py-1 text-xs font-bold text-white bg-green-500 rounded-full shadow"
                  initial={{ scale: 0, rotate: -180 }}
                  animate={{ scale: 1, rotate: 0 }}
                  transition={{ delay: 0.2, duration: 0.4, type: "spring" }}
                >
                  VEG
                </motion.span>
              )}
              {item.food_type === "vegan" && (
                <motion.span
                  className="px-2 py-1 text-xs font-bold text-white bg-green-600 rounded-full shadow"
                  initial={{ scale: 0, rotate: -180 }}
                  animate={{ scale: 1, rotate: 0 }}
                  transition={{ delay: 0.25, duration: 0.4, type: "spring" }}
                >
                  VEGAN
                </motion.span>
              )}
              {item.is_spice_applicable === "yes" && (
                <motion.span
                  className="px-2 py-1 text-xs font-bold text-white bg-red-500 rounded-full shadow"
                  initial={{ scale: 0, rotate: -180 }}
                  animate={{ scale: 1, rotate: 0 }}
                  transition={{ delay: 0.3, duration: 0.4, type: "spring" }}
                >
                  SPICY
                </motion.span>
              )}
            </div>
          )}
        </div>

        {/* Content */}
        <motion.div 
          className={contentContainer({ variant })}
          variants={staggerContainer}
          initial="hidden"
          animate="show"
        >
          {/* Title */}
          <motion.h3
            className={clsx(
              "font-bold text-gray-900 mb-2 sm:mb-3 line-clamp-2",
              variant === "prime"
                ? "text-base sm:text-lg lg:text-xl"
                : variant === "classic"
                ? "text-sm sm:text-base lg:text-lg"
                : "text-sm sm:text-base"
            )}
            variants={fadeInUp}
          >
            {item.name}
          </motion.h3>

          {/* Description */}
          {showDescription && item.description && (
            <motion.p
              className="text-gray-600 mb-3 sm:mb-4 text-xs sm:text-sm line-clamp-2 leading-relaxed"
              variants={fadeInUp}
            >
              {item.description}
            </motion.p>
          )}

          {/* Price & CTA - Responsive Layout */}
          <motion.div
            className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3"
            variants={fadeInUp}
          >
            {showPrice && (
              <span
                className={clsx(
                  "font-bold text-orange-600",
                  variant === "prime"
                    ? "text-lg sm:text-xl lg:text-2xl"
                    : variant === "classic"
                    ? "text-base sm:text-lg lg:text-xl"
                    : "text-sm sm:text-base lg:text-lg"
                )}
              >
                {formattedPrice}
              </span>
            )}

            {/* CTA */}
            {showAddToCart && (
              <motion.button
                onClick={handleAddToCart}
                aria-label={`Add ${item.name} to cart`}
                className="w-full sm:w-auto inline-flex items-center justify-center px-4 py-2 text-xs sm:text-sm font-medium rounded-xl shadow-sm focus:outline-none transition-colors text-white bg-[#F92C37] hover:bg-[#E01E2B]"
                initial={{ opacity: 0, y: 10 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.25, duration: 0.3 }}
                whileHover={{ scale: 1.05 }}
                whileTap={{ scale: 0.95 }}
              >
                ADD TO CART
              </motion.button>
            )}
          </motion.div>
        </motion.div>
      </motion.div>
    );
  }
);

PopularItemCard.displayName = "PopularItemCard";
export default PopularItemCard;
